import { lazy, Suspense, type JSX } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { AcceptInvitePage } from './auth/AcceptInvitePage';
import { LoginPage } from './auth/LoginPage';
import { SignupPage } from './auth/SignupPage';
import { RequireAuth } from './auth/RequireAuth';
import { RequireModuleAccess } from './auth/RequireModuleAccess';
import { MainLayout } from './layout/MainLayout';
import { RouteLoadingFallback } from './layout/RouteLoadingFallback';
import { PlatformLayout } from './platform/PlatformLayout';
import { RequirePlatformAccess } from './platform/RequirePlatformAccess';
import { PlatformWorkspaceHomePage } from './platform/PlatformWorkspaceHomePage';
import { RequirePlanUsable } from './billing/RequirePlanUsable';
import { CompanySelectionPage } from './pages/CompanySelectionPage';
import { CreateCompanyPage } from './pages/CreateCompanyPage';
import { WorkspaceHomePage } from './pages/WorkspaceHomePage';
import { RequireCompany } from './tenancy/RequireCompany';
import { RequireMemberships } from './tenancy/RequireMemberships';
import { theme } from './theme';

type RouteComponent = () => JSX.Element;

function lazyRoute<TExport extends string>(
  load: () => Promise<Record<TExport, RouteComponent>>,
  exportName: TExport,
): RouteComponent {
  const LazyPage = lazy<RouteComponent>(async () => {
    const routeModule = await load();
    return { default: routeModule[exportName] };
  });

  return function LazyRoute(): JSX.Element {
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <LazyPage />
      </Suspense>
    );
  };
}

const RegulatoryDirectoryCandidatePage = lazyRoute(
  () => import('./platform/regulatory-directory/RegulatoryDirectoryCandidatePage'),
  'RegulatoryDirectoryCandidatePage',
);
const RegulatoryDirectoryEntitiesPage = lazyRoute(
  () => import('./platform/regulatory-directory/RegulatoryDirectoryEntitiesPage'),
  'RegulatoryDirectoryEntitiesPage',
);
const RegulatoryDirectoryEntityPage = lazyRoute(
  () => import('./platform/regulatory-directory/RegulatoryDirectoryEntityPage'),
  'RegulatoryDirectoryEntityPage',
);
const RegulatoryDirectoryReviewQueuePage = lazyRoute(
  () => import('./platform/regulatory-directory/RegulatoryDirectoryReviewQueuePage'),
  'RegulatoryDirectoryReviewQueuePage',
);
const PlatformSystemOverviewPage = lazyRoute(
  () => import('./platform/system/PlatformSystemOverviewPage'),
  'PlatformSystemOverviewPage',
);
const PlatformBillingCatalogRedirectPage = lazyRoute(
  () => import('./platform/system/PlatformBillingCatalogPage'),
  'PlatformBillingCatalogRedirectPage',
);
const PlatformBillingCatalogPlansPage = lazyRoute(
  () => import('./platform/system/PlatformBillingCatalogPage'),
  'PlatformBillingCatalogPlansPage',
);
const PlatformBillingCatalogPlanDetailPage = lazyRoute(
  () => import('./platform/system/PlatformBillingCatalogPage'),
  'PlatformBillingCatalogPlanDetailPage',
);
const PlatformBillingCatalogFeaturesPage = lazyRoute(
  () => import('./platform/system/PlatformBillingCatalogPage'),
  'PlatformBillingCatalogFeaturesPage',
);
const PlatformMaterialsCatalogPage = lazyRoute(
  () => import('./platform/system/PlatformMaterialsCatalogPage'),
  'PlatformMaterialsCatalogPage',
);
const PlatformAuditLogsPage = lazyRoute(
  () => import('./platform/system/PlatformAuditLogsPage'),
  'PlatformAuditLogsPage',
);
const PlatformGrantsPage = lazyRoute(
  () => import('./platform/system/PlatformGrantsPage'),
  'PlatformGrantsPage',
);
const PlatformSystemCompanyDetailPage = lazyRoute(
  () => import('./platform/system/PlatformSystemCompanyDetailPage'),
  'PlatformSystemCompanyDetailPage',
);
const PlatformSystemCompanyStationsPage = lazyRoute(
  () => import('./platform/system/PlatformSystemCompanyStationsPage'),
  'PlatformSystemCompanyStationsPage',
);
const PlatformSystemCompanyStationDetailPage = lazyRoute(
  () => import('./platform/system/PlatformSystemCompanyStationsPage'),
  'PlatformSystemCompanyStationDetailPage',
);
const BillingPlaceholderPage = lazyRoute(
  () => import('./pages/BillingPlaceholderPage'),
  'BillingPlaceholderPage',
);
const AdminLocationStationsPage = lazyRoute(
  () => import('./pages/AdminLocationStationsPage'),
  'AdminLocationStationsPage',
);
const AdminLocationUnitProfilesPage = lazyRoute(
  () => import('./pages/admin-location-unit-profiles/AdminLocationUnitProfilesPage'),
  'AdminLocationUnitProfilesPage',
);
const AdminLocationInventoryPoliciesPage = lazyRoute(
  () => import('./pages/admin-location-inventory-policies/AdminLocationInventoryPoliciesPage'),
  'AdminLocationInventoryPoliciesPage',
);
const AdminLocationYardLocationsPage = lazyRoute(
  () => import('./pages/AdminLocationYardLocationsPage'),
  'AdminLocationYardLocationsPage',
);
const CompanySettingsPage = lazyRoute(
  () => import('./pages/CompanySettingsPage'),
  'CompanySettingsPage',
);
const PurchaseSettlementPolicyPage = lazyRoute(
  () => import('./pages/admin-purchase-settlement/PurchaseSettlementPolicyPage'),
  'PurchaseSettlementPolicyPage',
);
const PurchaseReceiptFolioSequencePage = lazyRoute(
  () => import('./pages/PurchaseReceiptFolioSequencePage'),
  'PurchaseReceiptFolioSequencePage',
);
const PurchaseAdjustmentPresetsPage = lazyRoute(
  () => import('./pages/PurchaseAdjustmentPresetsPage'),
  'PurchaseAdjustmentPresetsPage',
);
const AdminStoreLocationsPage = lazyRoute(
  () => import('./pages/AdminStoreLocationsPage'),
  'AdminStoreLocationsPage',
);
const AdminStoresPage = lazyRoute(() => import('./pages/AdminStoresPage'), 'AdminStoresPage');
const AdminTeamPage = lazyRoute(() => import('./pages/AdminTeamPage'), 'AdminTeamPage');
const AdminInvitesPage = lazyRoute(() => import('./pages/AdminInvitesPage'), 'AdminInvitesPage');
const CompanyRolesPage = lazyRoute(
  () => import('./pages/admin/CompanyRolesPage'),
  'CompanyRolesPage',
);
const OfflineExportPage = lazyRoute(
  () => import('./pages/admin/OfflineExportPage'),
  'OfflineExportPage',
);
const MaterialsCatalogPage = lazyRoute(
  () => import('./pages/admin/MaterialsCatalogPage'),
  'MaterialsCatalogPage',
);
const MaterialsPresentationPage = lazyRoute(
  () => import('./pages/admin/MaterialsPresentationPage'),
  'MaterialsPresentationPage',
);
const CompanyReceiversPage = lazyRoute(
  () => import('./pages/admin/CompanyReceiversPage'),
  'CompanyReceiversPage',
);
const CashPage = lazyRoute(() => import('./pages/CashPage'), 'CashPage');
const CustomersPage = lazyRoute(() => import('./pages/CustomersPage'), 'CustomersPage');
const EmployeesPage = lazyRoute(() => import('./pages/EmployeesPage'), 'EmployeesPage');
const HoldingUnitsPage = lazyRoute(
  () => import('./pages/holding-units/HoldingUnitsPage'),
  'HoldingUnitsPage',
);
const ShipmentDetailPage = lazyRoute(
  () => import('./pages/shipments/ShipmentDetailPage'),
  'ShipmentDetailPage',
);
const CoverSourcePreparationCreatePage = lazyRoute(
  () => import('./pages/shipments/CoverSourcePreparationCreatePage'),
  'CoverSourcePreparationCreatePage',
);
const CoverSourcePreparationRequestPage = lazyRoute(
  () => import('./pages/shipments/CoverSourcePreparationRequestPage'),
  'CoverSourcePreparationRequestPage',
);
const ShipmentsListPage = lazyRoute(
  () => import('./pages/shipments/ShipmentsListPage'),
  'ShipmentsListPage',
);
const ShipmentPreparationRequestPage = lazyRoute(
  () => import('./pages/shipments/ShipmentPreparationRequestPage'),
  'ShipmentPreparationRequestPage',
);
const ShipmentQuickCapturePage = lazyRoute(
  () => import('./pages/shipments/ShipmentQuickCapturePage'),
  'ShipmentQuickCapturePage',
);
const ShipmentSecureWeighCapturePage = lazyRoute(
  () => import('./pages/shipments/ShipmentSecureWeighCapturePage'),
  'ShipmentSecureWeighCapturePage',
);
const ShipmentsPage = lazyRoute(() => import('./pages/shipments/ShipmentsPage'), 'ShipmentsPage');
const TimeTrackingPage = lazyRoute(() => import('./pages/TimeTrackingPage'), 'TimeTrackingPage');
const TimeTrackingSelfClockPage = lazyRoute(
  () => import('./pages/TimeTrackingSelfClockPage'),
  'TimeTrackingSelfClockPage',
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
});

export function App(): JSX.Element {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route
                path="/capture/shipments/weigh/:linkId"
                element={<ShipmentSecureWeighCapturePage />}
              />
              <Route element={<RequireAuth />}>
                <Route element={<RequirePlatformAccess />}>
                  <Route element={<PlatformLayout />}>
                    <Route path="/platform" element={<PlatformWorkspaceHomePage />} />
                    <Route path="/platform/system" element={<PlatformSystemOverviewPage />} />
                    <Route
                      path="/platform/system/billing-catalog"
                      element={<PlatformBillingCatalogRedirectPage />}
                    />
                    <Route
                      path="/platform/system/billing-catalog/plans"
                      element={<PlatformBillingCatalogPlansPage />}
                    />
                    <Route
                      path="/platform/system/billing-catalog/plans/:planCode"
                      element={<PlatformBillingCatalogPlanDetailPage />}
                    />
                    <Route
                      path="/platform/system/billing-catalog/features"
                      element={<PlatformBillingCatalogFeaturesPage />}
                    />
                    <Route
                      path="/platform/system/materials-catalog"
                      element={<PlatformMaterialsCatalogPage />}
                    />
                    <Route path="/platform/system/audit-logs" element={<PlatformAuditLogsPage />} />
                    <Route
                      path="/platform/system/platform-grants"
                      element={<PlatformGrantsPage />}
                    />
                    <Route
                      path="/platform/system/companies/:companyId"
                      element={<PlatformSystemCompanyDetailPage />}
                    />
                    <Route
                      path="/platform/system/companies/:companyId/stations"
                      element={<PlatformSystemCompanyStationsPage />}
                    />
                    <Route
                      path="/platform/system/companies/:companyId/stations/:stationId"
                      element={<PlatformSystemCompanyStationDetailPage />}
                    />
                    <Route
                      path="/platform/regulatory-directory"
                      element={<RegulatoryDirectoryEntitiesPage />}
                    />
                    <Route
                      path="/platform/regulatory-directory/review"
                      element={<RegulatoryDirectoryReviewQueuePage />}
                    />
                    <Route
                      path="/platform/regulatory-directory/entities/new"
                      element={<RegulatoryDirectoryEntityPage />}
                    />
                    <Route
                      path="/platform/regulatory-directory/entities/:entityId"
                      element={<RegulatoryDirectoryEntityPage />}
                    />
                    <Route
                      path="/platform/regulatory-directory/candidates/:candidateId"
                      element={<RegulatoryDirectoryCandidatePage />}
                    />
                  </Route>
                </Route>
                <Route element={<RequireMemberships />}>
                  <Route element={<MainLayout />}>
                    <Route index element={<WorkspaceHomePage />} />
                    <Route path="/select-company" element={<CompanySelectionPage />} />
                    <Route path="/onboarding" element={<CreateCompanyPage />} />
                    <Route path="/billing" element={<BillingPlaceholderPage />} />
                    <Route element={<RequireCompany />}>
                      <Route
                        element={
                          <RequireModuleAccess module="company" moduleName="Company settings" />
                        }
                      >
                        <Route path="/admin/company" element={<CompanySettingsPage />} />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess
                            module="companyPurchaseSettlement"
                            moduleName="Purchase settlement policy"
                          />
                        }
                      >
                        <Route
                          path="/admin/company/purchase-settlement"
                          element={<PurchaseSettlementPolicyPage />}
                        />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess
                            module="companyPurchaseFolioSequence"
                            moduleName="Purchase receipt folio"
                          />
                        }
                      >
                        <Route
                          path="/admin/company/purchase-receipt-folio"
                          element={<PurchaseReceiptFolioSequencePage />}
                        />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess
                            module="companyPurchaseAdjustments"
                            moduleName="Purchase adjustment presets"
                          />
                        }
                      >
                        <Route
                          path="/admin/company/purchase-adjustments"
                          element={<PurchaseAdjustmentPresetsPage />}
                        />
                      </Route>
                      <Route element={<RequireModuleAccess module="stores" moduleName="Stores" />}>
                        <Route path="/admin/stores" element={<AdminStoresPage />} />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess module="storeLocations" moduleName="Locations" />
                        }
                      >
                        <Route
                          path="/admin/stores/:storeId/locations"
                          element={<AdminStoreLocationsPage />}
                        />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess module="locationStations" moduleName="Stations" />
                        }
                      >
                        <Route
                          path="/admin/locations/:locationId/stations"
                          element={<AdminLocationStationsPage />}
                        />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess
                            module="locationYardLocations"
                            moduleName="Yard locations"
                          />
                        }
                      >
                        <Route
                          path="/admin/locations/:locationId/yard-locations"
                          element={<AdminLocationYardLocationsPage />}
                        />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess
                            module="locationUnitProfiles"
                            moduleName="Unit profiles"
                          />
                        }
                      >
                        <Route
                          path="/admin/locations/:locationId/unit-profiles"
                          element={<AdminLocationUnitProfilesPage />}
                        />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess
                            module="locationInventoryPolicies"
                            moduleName="Inventory policies"
                          />
                        }
                      >
                        <Route
                          path="/admin/locations/:locationId/inventory-policies"
                          element={<AdminLocationInventoryPoliciesPage />}
                        />
                      </Route>
                      <Route element={<RequireModuleAccess module="team" moduleName="Team" />}>
                        <Route path="/admin/team" element={<AdminTeamPage />} />
                      </Route>
                      <Route
                        element={<RequireModuleAccess module="invites" moduleName="Invites" />}
                      >
                        <Route path="/admin/invites" element={<AdminInvitesPage />} />
                      </Route>
                      <Route
                        element={<RequireModuleAccess module="companyRoles" moduleName="Roles" />}
                      >
                        <Route path="/admin/roles" element={<CompanyRolesPage />} />
                      </Route>
                      <Route
                        element={
                          <RequireModuleAccess module="offline" moduleName="Offline export" />
                        }
                      >
                        <Route path="/admin/offline/export" element={<OfflineExportPage />} />
                      </Route>
                      <Route element={<RequirePlanUsable />}>
                        <Route
                          element={
                            <RequireModuleAccess
                              module="materialsCatalog"
                              moduleName="Materials catalog"
                            />
                          }
                        >
                          <Route
                            path="/admin/materials-catalog"
                            element={<MaterialsCatalogPage />}
                          />
                          <Route
                            path="/admin/materials-presentation"
                            element={<MaterialsPresentationPage />}
                          />
                        </Route>
                        <Route
                          element={
                            <RequireModuleAccess
                              module="companyReceivers"
                              moduleName="Company receivers"
                            />
                          }
                        >
                          <Route
                            path="/admin/company-receivers"
                            element={<CompanyReceiversPage />}
                          />
                        </Route>
                        <Route element={<RequireModuleAccess module="cash" moduleName="Cash" />}>
                          <Route path="/cash" element={<CashPage />} />
                        </Route>
                        <Route
                          element={
                            <RequireModuleAccess module="customers" moduleName="Customers" />
                          }
                        >
                          <Route path="/customers" element={<CustomersPage />} />
                        </Route>
                        <Route
                          element={
                            <RequireModuleAccess module="shipments" moduleName="Shipments" />
                          }
                        >
                          <Route path="/shipments" element={<ShipmentsListPage />} />
                          <Route path="/shipments/create" element={<ShipmentsPage />} />
                          <Route
                            path="/shipments/source-preparation/cover/create"
                            element={<CoverSourcePreparationCreatePage />}
                          />
                          <Route
                            path="/shipments/source-preparation/cover/:requestId"
                            element={<CoverSourcePreparationRequestPage />}
                          />
                          <Route
                            path="/shipments/requests/:requestId"
                            element={<ShipmentPreparationRequestPage />}
                          />
                          <Route
                            path="/shipments/:shipmentId/continue"
                            element={<ShipmentDetailPage />}
                          />
                          <Route path="/shipments/:shipmentId" element={<ShipmentDetailPage />} />
                        </Route>
                        <Route
                          path="/shipments/quick-capture"
                          element={<ShipmentQuickCapturePage />}
                        />
                        <Route
                          element={
                            <RequireModuleAccess module="holdingUnits" moduleName="Holding units" />
                          }
                        >
                          <Route path="/holding-units" element={<HoldingUnitsPage />} />
                        </Route>
                        <Route
                          element={
                            <RequireModuleAccess module="employees" moduleName="Employees" />
                          }
                        >
                          <Route path="/employees" element={<EmployeesPage />} />
                        </Route>
                        <Route
                          element={
                            <RequireModuleAccess module="timeTracking" moduleName="Time tracking" />
                          }
                        >
                          <Route path="/time-tracking" element={<TimeTrackingPage />} />
                        </Route>
                        <Route
                          element={
                            <RequireModuleAccess
                              module="timeTrackingSelf"
                              moduleName="Self clock"
                            />
                          }
                        >
                          <Route
                            path="/time-tracking/self"
                            element={<TimeTrackingSelfClockPage />}
                          />
                        </Route>
                      </Route>
                    </Route>
                    <Route path="*" element={<WorkspaceHomePage />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
